import { useEffect, useState } from "react";
import api from "../../lib/api";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";
import { BarChart3, Eye, MousePointerClick, Users2 } from "lucide-react";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 Days" },
  { key: "30d", label: "Last 30 Days" },
  { key: "all", label: "All Time" },
];

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`analytics-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#F0F4F8] text-[#4A6FA5]">
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </span>
        <div>
          <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</p>
          <p className="font-heading text-3xl text-slate-900 mt-0.5">{value ?? "—"}</p>
        </div>
      </div>
      {sub && <p className="mt-3 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

function RankedList({ title, rows, keyLabel = "Path", emptyLabel = "No data yet." }) {
  const testKey = title.toLowerCase().replace(/[^a-z]+/g, "-");
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`analytics-list-${testKey}`}>
      <h3 className="font-heading text-lg text-slate-900">{title}</h3>
      <div className="mt-3">
        {(!rows || rows.length === 0) && (
          <p className="text-sm text-slate-500">{emptyLabel}</p>
        )}
        {rows && rows.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="py-2 font-semibold">{keyLabel}</th>
                <th className="py-2 font-semibold text-right">Views</th>
                <th className="py-2 font-semibold text-right">Unique</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.key}>
                  <td className="py-2 text-slate-800 truncate max-w-[280px]" title={r.key}>{r.key}</td>
                  <td className="py-2 text-slate-700 text-right tabular-nums">{r.views}</td>
                  <td className="py-2 text-slate-500 text-right tabular-nums">{r.unique}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .get(`/admin/analytics/summary?range=${range}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setError("Failed to load analytics."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range]);

  const daily = data?.daily || [];
  const weekly = data?.weekly || [];
  const monthly = data?.monthly || [];

  return (
    <div data-testid="admin-analytics">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl tracking-tight text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-[#4A6FA5]" strokeWidth={1.75} /> Website Analytics
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Anonymous public-site usage. No names, emails, phone numbers, appointment details, or medical content are collected.
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden" role="tablist" data-testid="analytics-range-tabs">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              role="tab"
              aria-selected={range === r.key}
              onClick={() => setRange(r.key)}
              data-testid={`analytics-range-${r.key}`}
              className={`px-3.5 py-2 text-sm font-medium transition-colors ${
                range === r.key ? "bg-[#4A6FA5] text-white" : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="mt-6 text-sm text-red-600" data-testid="analytics-error">{error}</p>}

      {loading && <p className="mt-6 text-sm text-slate-500" data-testid="analytics-loading">Loading…</p>}

      {!loading && data && (
        <>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard icon={Users2} label="Total Visits" value={data.total_visits} sub="Unique browser sessions" />
            <StatCard icon={Eye} label="Page Views" value={data.total_page_views} />
            <StatCard icon={MousePointerClick} label="CTA Clicks" value={(data.top_ctas || []).reduce((a, r) => a + r.views, 0)} />
          </div>

          {range !== "all" && daily.length > 0 && (
            <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5" data-testid="analytics-daily-chart">
              <h3 className="font-heading text-lg text-slate-900">Daily Trend</h3>
              <div className="h-64 mt-3">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="visits" fill="#4A6FA5" name="Visits" />
                    <Bar dataKey="page_views" fill="#94A3B8" name="Page views" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="mt-6 grid lg:grid-cols-2 gap-4">
            {weekly.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="analytics-weekly-chart">
                <h3 className="font-heading text-lg text-slate-900">Weekly Trend</h3>
                <div className="h-56 mt-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={weekly}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#64748B" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                      <Tooltip />
                      <Line type="monotone" dataKey="visits" stroke="#4A6FA5" strokeWidth={2} name="Visits" />
                      <Line type="monotone" dataKey="page_views" stroke="#94A3B8" strokeWidth={2} name="Page views" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
            {monthly.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="analytics-monthly-chart">
                <h3 className="font-heading text-lg text-slate-900">Monthly Trend</h3>
                <div className="h-56 mt-3">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={monthly}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748B" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                      <Tooltip />
                      <Line type="monotone" dataKey="visits" stroke="#4A6FA5" strokeWidth={2} name="Visits" />
                      <Line type="monotone" dataKey="page_views" stroke="#94A3B8" strokeWidth={2} name="Page views" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 grid lg:grid-cols-3 gap-4">
            <RankedList title="Most Visited Pages" rows={data.top_pages} keyLabel="Path" />
            <RankedList title="Most Viewed Sections" rows={data.top_sections} keyLabel="Section" />
            <RankedList title="Most Clicked CTAs" rows={data.top_ctas} keyLabel="CTA" />
          </div>

          <p className="mt-6 text-xs text-slate-500">
            Data refreshes when you change the range. Section views are counted only when a visitor scrolls the section into view.
          </p>
        </>
      )}
    </div>
  );
}
